import useApi from "../services/ApiService";
import { extractApiErrorMessage } from "../lib/apiError";
import { Paging } from "../models/Paging";
import { MigrationType } from "../models/Statistics/MigrationType";
import {
  RenamedFolderFinding,
  RequestFolderSweepResult,
  ShadowFolderFinding,
} from "../models/RenamedFolderFinding";

export interface GetRenamedFolderFindingsOptions {
  entityType?: MigrationType | null;
  /** True for findings still out of step, false for absorbed ones, null for both. */
  unresolved?: boolean | null;
  search?: string | null;
  pageNumber?: number | null;
  pageSize?: number | null;
  descending?: boolean;
}

export interface GetShadowFolderFindingsOptions {
  entityType?: MigrationType | null;
  /** True for folders tied to an entity, false for those tied to none, null for both. */
  attributed?: boolean | null;
  search?: string | null;
  pageNumber?: number | null;
  pageSize?: number | null;
  descending?: boolean;
}

export class RenamedFoldersRepository {
  private api = useApi();

  async getRenamedFolderFindings(
    options: GetRenamedFolderFindingsOptions = {}
  ): Promise<{ data: RenamedFolderFinding[]; paging: Paging | null }> {
    const {
      entityType = null,
      unresolved = null,
      search = null,
      pageNumber = null,
      pageSize = null,
      descending = true,
    } = options;

    try {
      const response = await this.api.get("/api/Drift/RenamedFolders", {
        params: {
          EntityType: entityType,
          Unresolved: unresolved,
          Search: search,
          PageNumber: pageNumber,
          PageSize: pageSize,
          Descending: descending,
        },
        paramsSerializer: { indexes: true },
      });

      return { data: response.data, paging: readPaging(response.headers) };
    } catch (error) {
      throw new Error("Failed to fetch renamed folders");
    }
  }

  async getShadowFolderFindings(
    options: GetShadowFolderFindingsOptions = {}
  ): Promise<{ data: ShadowFolderFinding[]; paging: Paging | null }> {
    const {
      entityType = null,
      attributed = null,
      search = null,
      pageNumber = null,
      pageSize = null,
      descending = true,
    } = options;

    try {
      const response = await this.api.get("/api/Drift/ShadowFolders", {
        params: {
          EntityType: entityType,
          Attributed: attributed,
          Search: search,
          PageNumber: pageNumber,
          PageSize: pageSize,
          Descending: descending,
        },
        paramsSerializer: { indexes: true },
      });

      return { data: response.data, paging: readPaging(response.headers) };
    } catch (error) {
      throw new Error("Failed to fetch untracked folders");
    }
  }

  async requestFolderSweep(reason?: string): Promise<RequestFolderSweepResult> {
    try {
      const response = await this.api.post("/api/Drift/RequestSweep", { reason });
      return response.data as RequestFolderSweepResult;
    } catch (error) {
      // The backend reason matters here: the org may have no clients, or the caller may lack permission to it.
      throw new Error(extractApiErrorMessage(error, "Failed to request a sweep."));
    }
  }
}

function readPaging(headers: Record<string, unknown>): Paging | null {
  const header = headers["x-pagination"];
  if (!header || typeof header !== "string") return null;

  try {
    return JSON.parse(header) as Paging;
  } catch {
    return null;
  }
}

export default RenamedFoldersRepository;
